import { Sparkline } from '../../charts'
import { trendValues } from '../ceoCockpitModel'
import { formatCurrencyExact, formatDays, formatMonths, trimStr } from '../ceoFormat'
import type { CeoCockpitResponse } from '../ceoCockpitTypes'

const NOT_PREPARED = 'Not prepared'

function money(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? NOT_PREPARED : formatCurrencyExact(value)
}

function friendlyWeek(value: string | null | undefined): string | null {
  const trimmed = trimStr(value)
  if (!trimmed) return null
  const date = new Date(`${trimmed.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(date.getTime())) return trimmed
  return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}

export function CeoCashStrip({ payload }: { payload: CeoCockpitResponse }) {
  const cash = payload.cash
  if (!cash) return null
  const cashNow = cash.cash_estimated_as_of ?? cash.cash_at_close
  const series = trendValues(cash.series_13w)
  const collision = friendlyWeek(cash.first_negative_week)
  const low = cash.min_projected_cash_13w
  const burning =
    cash.runway_months != null &&
    Number.isFinite(cash.runway_months) &&
    cash.burn_avg_3m != null &&
    Number.isFinite(cash.burn_avg_3m)
  return (
    <section className="ceo-section ceo-cash-strip rise" id="ceo-cash" data-testid="ceo-cash" data-fg-tour="ceo-cash">
      <p className="ceo-kicker">Cash &amp; runway</p>
      <div className="ceo-cash-band">
        <article className={cashNow == null ? 'is-empty' : ''}>
          <span className="ceo-kicker">Cash</span>
          <b>{money(cashNow)}</b>
          <small>
            {cashNow == null
              ? 'no balance yet'
              : cash.estimated
                ? 'estimated as of yesterday'
                : cash.close_period
                  ? `at close, ${cash.close_period}`
                  : 'at close'}
          </small>
        </article>
        {/* Runway is a burn metric. A profitable company has none, so the tile
            answers the question it CAN answer — how long the balance lasts at
            the current spend rate — instead of reading "Not prepared". */}
        {burning ? (
          <article>
            <span className="ceo-kicker">Runway</span>
            <b>{formatMonths(cash.runway_months)}</b>
            <small>{`${formatCurrencyExact(cash.burn_avg_3m as number)} / mo burn, 3-mo avg`}</small>
          </article>
        ) : cash.months_of_cash != null && Number.isFinite(cash.months_of_cash) ? (
          <article>
            <span className="ceo-kicker">Cash on hand</span>
            <b>{formatMonths(cash.months_of_cash)}</b>
            <small>
              {cash.monthly_outflow_avg_3m != null && Number.isFinite(cash.monthly_outflow_avg_3m)
                ? `at ${formatCurrencyExact(cash.monthly_outflow_avg_3m)} / mo spend, 3-mo avg`
                : 'at the current spend rate'}
            </small>
          </article>
        ) : null}
        <article className={collision ? 'is-warn' : ''}>
          <span className="ceo-kicker">Next collision</span>
          <b>{collision || 'None in 13 weeks'}</b>
          <small>
            {cash.min_cash_floor != null && Number.isFinite(cash.min_cash_floor)
              ? `floor ${formatCurrencyExact(cash.min_cash_floor)}${cash.min_cash_floor_basis ? ` — ${cash.min_cash_floor_basis}` : ''}`
              : 'no floor set'}
          </small>
        </article>
        <article className={low == null ? 'is-empty' : ''}>
          <span className="ceo-kicker">13-week low</span>
          <b>{money(low)}</b>
          <small>
            {cash.dso != null && Number.isFinite(cash.dso)
              ? `DSO ${formatDays(cash.dso)}${cash.dso_delta != null && Number.isFinite(cash.dso_delta) ? ` (${cash.dso_delta > 0 ? '+' : ''}${Math.round(cash.dso_delta)})` : ''}`
              : cash.ar_open != null
                ? `A/R open ${formatCurrencyExact(cash.ar_open)}`
                : 'projected'}
          </small>
        </article>
        <div className="ceo-cash-spark" aria-hidden={series.length < 2 ? true : undefined}>
          {series.length >= 2 ? (
            <Sparkline
              values={series}
              width={180}
              height={40}
              benchmark={cash.min_cash_floor}
              endDot
              ariaLabel="13-week projected cash"
            />
          ) : (
            <span className="ceo-muted">13-week curve not prepared</span>
          )}
        </div>
      </div>
    </section>
  )
}
